import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  useTheme,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { KPILimit, useKPIStore } from '../../stores/kpi.store';
import { StatisticType } from '../../types/dashboard.type';
import { getStatisticLabel } from '../../helpers/metric-i18n.helper';
import { SetupCoachmark } from '../selfSetup/SetupCoachmark';

interface KPILimitsDialogProps {
  open: boolean;
  onClose: () => void;
  tourTip?: 'kpiForm' | 'kpiList' | null;
  onTourNext?: () => void;
  onTourDismiss?: () => void;
  tourNextLabel?: string;
  tourFormTitle?: string;
  tourFormSubtitle?: string;
  tourListTitle?: string;
  tourListSubtitle?: string;
}

const KPILimitsDialog: React.FC<KPILimitsDialogProps> = ({
  open,
  onClose,
  tourTip = null,
  onTourNext,
  onTourDismiss,
  tourNextLabel,
  tourFormTitle,
  tourFormSubtitle,
  tourListTitle,
  tourListSubtitle,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { limits, addLimit, removeLimit, toggleLimit } = useKPIStore();
  const [newLimit, setNewLimit] = useState<Partial<KPILimit>>({
    name: '',
    value: 0,
    operator: '>',
    alertMessage: '',
    isEnabled: true,
  });

  const handleAddLimit = () => {
    if (newLimit.name && newLimit.alertMessage) {
      addLimit({
        id: Math.random().toString(36).substr(2, 9),
        name: newLimit.name as StatisticType,
        value: newLimit.value || 0,
        operator: newLimit.operator || '>',
        alertMessage: newLimit.alertMessage,
        isEnabled: true,
      });
      setNewLimit({
        name: '',
        value: 0,
        operator: '>',
        alertMessage: '',
        isEnabled: true,
      });
    }
  };

  const operatorLabel = (op: string) => {
    switch (op) {
      case '>':
        return t('kpiLimits.operators.gt');
      case '<':
        return t('kpiLimits.operators.lt');
      case '=':
        return t('kpiLimits.operators.eq');
      case '>=':
        return t('kpiLimits.operators.gte');
      case '<=':
        return t('kpiLimits.operators.lte');
      default:
        return op;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      hideBackdrop={Boolean(tourTip)}
      PaperProps={{
        sx: {
          bgcolor: theme.palette.background.paper,
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          borderRadius: '16px 16px 0 0',
        }}
      >
        {t('kpiLimits.titleFull')}
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <SetupCoachmark
          open={tourTip === 'kpiForm'}
          title={tourFormTitle ?? t('selfSetup.tour.dashboardKpiFormTitle')}
          subtitle={tourFormSubtitle ?? t('selfSetup.tour.dashboardKpiFormSubtitle')}
          onDismiss={() => onTourDismiss?.()}
          onNext={() => onTourNext?.()}
          nextLabel={tourNextLabel ?? t('selfSetup.tourNext')}
          fullWidth
        >
          <Box id="self-setup-kpi-form" sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('kpiLimits.addNewLimit')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>{t('kpiLimits.kpi')}</InputLabel>
                <Select
                  value={newLimit.name}
                  label={t('kpiLimits.kpi')}
                  onChange={(e) => setNewLimit({ ...newLimit, name: e.target.value })}
                >
                  {Object.values(StatisticType).map((stat) => (
                    <MenuItem key={stat} value={stat}>
                      {getStatisticLabel(t, stat)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>{t('kpiLimits.operator')}</InputLabel>
                <Select
                  value={newLimit.operator}
                  label={t('kpiLimits.operator')}
                  onChange={(e) =>
                    setNewLimit({
                      ...newLimit,
                      operator: e.target.value as KPILimit['operator'],
                    })
                  }
                >
                  <MenuItem value=">">{t('kpiLimits.operators.gt')}</MenuItem>
                  <MenuItem value="<">{t('kpiLimits.operators.lt')}</MenuItem>
                  <MenuItem value="=">{t('kpiLimits.operators.eq')}</MenuItem>
                  <MenuItem value=">=">{t('kpiLimits.operators.gte')}</MenuItem>
                  <MenuItem value="<=">{t('kpiLimits.operators.lte')}</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label={t('kpiLimits.value')}
                type="number"
                value={newLimit.value}
                onChange={(e) => setNewLimit({ ...newLimit, value: Number(e.target.value) })}
                sx={{ minWidth: 120 }}
              />
              <TextField
                label={t('kpiLimits.alertMessage')}
                value={newLimit.alertMessage}
                onChange={(e) => setNewLimit({ ...newLimit, alertMessage: e.target.value })}
                sx={{ minWidth: 300 }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddLimit}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark,
                  },
                }}
              >
                {t('kpiLimits.addLimit')}
              </Button>
            </Box>
          </Box>
        </SetupCoachmark>

        <SetupCoachmark
          open={tourTip === 'kpiList'}
          title={tourListTitle ?? t('selfSetup.tour.dashboardKpiListTitle')}
          subtitle={tourListSubtitle ?? t('selfSetup.tour.dashboardKpiListSubtitle')}
          onDismiss={() => onTourDismiss?.()}
          onNext={() => onTourNext?.()}
          nextLabel={tourNextLabel ?? t('selfSetup.tourNext')}
          fullWidth
        >
          <Box id="self-setup-kpi-list">
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('kpiLimits.currentLimits')}
            </Typography>
            <List>
              {limits.map((limit) => (
                <ListItem
                  key={limit.id}
              sx={{
                bgcolor: theme.palette.background.default,
                mb: 1,
                borderRadius: '8px',
              }}
            >
              <ListItemText
                primary={
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {getStatisticLabel(t, limit.name)}
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    {t('kpiLimits.alertWhen', {
                      name: getStatisticLabel(t, limit.name),
                      operator: operatorLabel(limit.operator),
                      value: limit.value,
                    })}
                    {limit.alertMessage && ` - ${limit.alertMessage}`}
                  </Typography>
                }
              />
              <ListItemSecondaryAction>
                <Switch
                  edge="end"
                  checked={limit.isEnabled}
                  onChange={() => toggleLimit(limit.id)}
                  sx={{ mr: 2 }}
                />
                <IconButton
                  edge="end"
                  onClick={() => removeLimit(limit.id)}
                  sx={{ color: theme.palette.error.main }}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
            </List>
          </Box>
        </SetupCoachmark>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={onClose}
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              color: theme.palette.text.primary,
            },
          }}
        >
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KPILimitsDialog;
